import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { requestInloglink, type InloglinkRequestStatus } from "./localAccount";

/** Matches the API: a link the person asks for is valid for 30 minutes. */
const GELDIG_MINUTEN = 30;

/**
 * An address, and a link in the mailbox. The answer never says whether the address is known - an
 * honest one is a membership oracle - so the confirmation names it back and says *if we know it*.
 * See docs/adr/0066-an-inloglink-is-the-fourth-door-and-a-password-becomes-optional.md.
 */
export function Inloglink() {
  const { t } = useTranslation(["session", "common"]);
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<InloglinkRequestStatus>();
  const [sentTo, setSentTo] = useState("");
  const [again, setAgain] = useState(false);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  async function send(event?: React.FormEvent) {
    event?.preventDefault();
    setBusy(true);
    setError(undefined);

    try {
      // Where they were going, so the link lands there rather than on the front door.
      const result = await requestInloglink(email, window.location.pathname);
      setSentTo(email);
      setAgain(status === "verstuurd");
      setStatus(result.status);
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : t("inloglink.errors.failed"),
      );
    } finally {
      setBusy(false);
    }
  }

  if (status === "te-vaak") {
    return (
      <InloglinkMelding
        titel={t("inloglink.tooOften.title")}
        tekst={t("inloglink.tooOften.body", { mailbox: t("common:mailbox") })}
      />
    );
  }

  if (status === "niet-beschikbaar") {
    return (
      <InloglinkMelding
        titel={t("inloglink.unavailable.title")}
        tekst={t("inloglink.unavailable.body", {
          mailbox: t("common:mailbox"),
        })}
      />
    );
  }

  if (status === "verstuurd") {
    return (
      <InloglinkVerstuurd
        sentTo={sentTo}
        again={again}
        busy={busy}
        onResend={() => void send()}
      />
    );
  }

  return (
    <form className="space-y-3" onSubmit={(event) => void send(event)}>
      <p className="text-sm text-muted-foreground">
        {t("inloglink.what", { minuten: GELDIG_MINUTEN })}
      </p>

      <div className="space-y-1.5">
        <Label htmlFor="inloglink-adres">{t("local.email")}</Label>
        <Input
          id="inloglink-adres"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <Button type="submit" disabled={busy} data-testid="signin-inloglink">
        {busy ? t("common:actions.busy") : t("inloglink.submit")}
      </Button>
    </form>
  );
}

function InloglinkMelding({ titel, tekst }: { titel: string; tekst: string }) {
  return (
    <div className="space-y-2 text-sm">
      <p className="font-medium">{titel}</p>
      <p className="text-muted-foreground">{tekst}</p>
    </div>
  );
}

function InloglinkVerstuurd({
  sentTo,
  again,
  busy,
  onResend,
}: {
  sentTo: string;
  again: boolean;
  busy: boolean;
  onResend: () => void;
}) {
  const { t } = useTranslation(["session", "common"]);

  return (
    <div className="space-y-3 text-sm">
      <p className="font-medium">{t("inloglink.sent.title")}</p>
      <p className="text-muted-foreground">
        {t("inloglink.sent.body", { adres: sentTo })}
      </p>
      {again && (
        <p className="rounded-md border border-brand-blue/40 bg-brand-blue/5 p-3">
          {t("inloglink.sent.again", { minuten: GELDIG_MINUTEN })}
        </p>
      )}
      <p className="text-muted-foreground">
        {t("inloglink.sent.noMail", { mailbox: t("common:mailbox") })}
      </p>
      <Button variant="outline" disabled={busy} onClick={onResend}>
        {busy ? t("common:actions.busy") : t("inloglink.sent.resend")}
      </Button>
    </div>
  );
}
